import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  input,
  signal,
} from '@angular/core';
import { PanelHeaderComponent } from './panel-header/panel-header.component';
import { PanelContentComponent } from './panel-content/panel-content.component';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'ri-resolution-ui-panel',
  imports: [NgTemplateOutlet],
  template: `
    <div class="cursor-pointer" [class.cursor-auto]="disableExpand()">
      <h3 [id]="headerId" class="m-0 mb-2" (click)="toggle()">
        <div
          role="button"
          class="flex items-center gap-x-2 text-left transition-all [&[aria-expanded=true]>i]:rotate-90"
          [attr.aria-expanded]="expanded()"
          [attr.aria-controls]="contentId"
          tabindex="0"
          (keydown.enter)="toggle()"
          (keydown.space)="toggle(); $event.preventDefault()"
        >
          <i
            class="pi pi-chevron-right transition-transform duration-200"
            [class.text-gray-300]="disableExpand()"
            [class.text-white]="hideExpand()"
          ></i>
          <div class="grow">
            @if (header()?.template()) {
              <ng-container
                [ngTemplateOutlet]="header()!.template()!"
              ></ng-container>
            }
          </div>
        </div>
      </h3>

      <div
        role="region"
        class="grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 [&[aria-hidden=false]]:grid-rows-[1fr]"
        [id]="contentId"
        [attr.aria-hidden]="!expanded()"
        [attr.aria-labeledby]="headerId"
      >
        <div class="overflow-hidden">
          <ng-content select="ri-resolution-ui-panel-content"></ng-content>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelComponent {
  readonly disableExpand = input<boolean>(false);
  readonly hideExpand = input<boolean>(false);
  readonly expand = input<boolean>(true);

  readonly header = contentChild(PanelHeaderComponent);
  readonly content = contentChild(PanelContentComponent);

  private readonly _expanded = signal<boolean>(true);
  public readonly expanded = this._expanded.asReadonly();

  private readonly id = crypto.randomUUID();
  readonly headerId = `panel-${this.id}-header`;
  readonly contentId = `panel-${this.id}-content`;

  toggle() {
    if (this.disableExpand()) {
      return;
    }
    this._expanded.update((expaned: boolean) => !expaned);
  }

  collapse() {
    if (this.disableExpand()) {
      return;
    }
    this._expanded.set(false);
  }
}
